import { useGetInfinityUsers } from "@/api/queries/users";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";

interface UserSelectProps {
  value?: number;
  onValueChange?: (value: number | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  userRole?: string;
}

export const UserSelect = ({
  value,
  onValueChange,
  placeholder = "Select user...",
  disabled = false,
  className,
  userRole,
}: UserSelectProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const { data, fetchNextPage, isLoading, isFetching } = useGetInfinityUsers({
    pageSize: 50,
    ...(search?.length && { username: search?.toLowerCase() ?? "" }),
    ...(userRole && { role: userRole }),
  });

  const allUsers = useMemo(() => {
    const pages = data?.pages ?? [];

    return pages.flatMap((page) => page.data);
  }, [data]);

  const selectedUser = allUsers.find((user) => user.id === value);
  const pagination = data?.pages[0]?.pagination;
  const hasMore = pagination
    ? (pagination.page + 1) * pagination.pageSize < pagination.total
    : false;

  const handleLoadMore = async () => {
    await fetchNextPage();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("w-full justify-between", className)}
        >
          {selectedUser ? <span className="truncate">{selectedUser.username}</span> : placeholder}
          <div className="ml-2 flex items-center gap-1 shrink-0">
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search users..." value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>
              {isLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              ) : (
                "No users found."
              )}
            </CommandEmpty>
            <CommandGroup>
              {allUsers.map((user) => (
                <CommandItem
                  key={user.id}
                  value={String(user.id)}
                  onSelect={() => {
                    if (selectedUser?.id === user.id) {
                      onValueChange?.(undefined);
                    } else {
                      onValueChange?.(user.id);
                      setOpen(false);
                    }
                  }}
                >
                  <Check
                    className={cn("mr-2 h-4 w-4", value === user.id ? "opacity-100" : "opacity-0")}
                  />
                  <div className="flex flex-col">
                    <span>{user.username}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            {hasMore && (
              <div className="p-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  onClick={handleLoadMore}
                  disabled={isFetching}
                >
                  {isFetching ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    "Load more"
                  )}
                </Button>
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
